import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import { useModal } from '../components/useModal';
import { validity } from '../format';

/**
 * Salto master codes — the weekly code support reads out to somebody standing
 * outside a locked door.
 *
 * One per SALTO facility, rotated automatically every Monday. Regenerating is
 * the reason this page exists: a code that has leaked has to stop working now,
 * not on Monday.
 *
 * Two things the old page did not say, and this one does:
 *
 * - **A code has an expiry, and an expired code is not a code.** The old page
 *   printed `endTimeUtc` through `toLocaleString()` next to a bold code, with
 *   nothing to distinguish a code valid for six more days from one that ran out
 *   yesterday. Support reading out a dead code is the failure this screen is
 *   supposed to prevent, so the row says how long is left and an expired one is
 *   marked.
 * - **"Regenerate all" is not a refresh.** It invalidates every facility's code
 *   at once — every code anybody was told this week stops working. The old page
 *   asked through `confirm()`; this one says the count and what breaks.
 *
 * The codes themselves are secrets on screen. They are printed large, because
 * that is the job, but each has its own copy button so nobody retypes one into
 * a chat window with a digit wrong.
 */

const parseDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const fmtDate = value => {
  const date = parseDate(value);
  return date ? date.toLocaleString() : '—';
};

const MasterCodesInternal = ({ api }) => {
  const { ask, modalProps } = useModal();

  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busyFacilityId, setBusyFacilityId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  const [note, setNote] = useState(null);
  const [copied, setCopied] = useState(null);
  const [filter, setFilter] = useState('');

  // Read once per render rather than per row, so every row on screen agrees
  // about what "now" is.
  const now = Date.now();

  const load = useCallback(async () => {
    try {
      const response = await api.saltoMasterCodes.getMasters();
      if (response?.success === false) {
        setFailed(true);
        return;
      }
      setRows(Array.isArray(response?.data) ? response.data : []);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (code, facilityId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(facilityId);
      setTimeout(() => setCopied(current => (current === facilityId ? null : current)), 1400);
    } catch (error) {
      // Clipboard is blocked in some contexts; the code is on screen either way.
    }
  };

  const regenerate = row => ask({
    kind: 'danger',
    title: `Regenerate the code for ${row.facilityName || 'this facility'}?`,
    context: 'Salto master codes',
    body: 'A new code is generated and installed now. The current code stops opening the door immediately — anybody who was given it this week has to be told the new one.',
    confirmLabel: 'Regenerate',
    onConfirm: async () => {
      setBusyFacilityId(row.facilityId);
      try {
        const response = await api.saltoMasterCodes.rotateMaster(row.facilityId);
        if (response?.success === false) {
          setNote({ tone: 'error', text: `The code was not regenerated. ${response.message || ''}`.trim() });
          throw new Error('rotate failed');
        }
        await load();
        setNote({ tone: 'info', text: `New code for ${row.facilityName || row.facilityId}.` });
      } finally {
        setBusyFacilityId(null);
      }
    }
  });

  const regenerateAll = () => ask({
    kind: 'danger',
    title: 'Regenerate every master code?',
    context: 'Salto master codes',
    body: `Every SALTO facility gets a new code${rows ? ` — ${rows.length} of them` : ''}. Every code anybody has been given this week stops working at once, including the ones support read out today.`,
    warning: 'There is no way to get the old codes back.',
    confirmLabel: 'Regenerate all',
    onConfirm: async () => {
      setBusyAll(true);
      try {
        const response = await api.saltoMasterCodes.rotateAllMasters();
        if (response?.success === false) {
          setNote({ tone: 'error', text: `The codes were not regenerated. ${response.message || ''}`.trim() });
          throw new Error('rotate all failed');
        }
        await load();
        setNote({ tone: 'info', text: 'Every master code has been regenerated.' });
      } finally {
        setBusyAll(false);
      }
    }
  });

  const visible = useMemo(() => {
    if (!rows) return [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(row => (
      String(row.facilityName || '').toLowerCase().includes(needle)
      || String(row.facilityId || '').toLowerCase().includes(needle)
      || String(row.code || '').toLowerCase().includes(needle)
    ));
  }, [rows, filter]);

  const problems = useMemo(() => {
    if (!rows) return { missing: 0, expired: 0 };
    return rows.reduce((acc, row) => {
      if (!row.code) acc.missing += 1;
      else {
        const end = parseDate(row.endTimeUtc);
        if (end && end.getTime() < now) acc.expired += 1;
      }
      return acc;
    }, { missing: 0, expired: 0 });
  }, [rows, now]);

  if (failed) {
    return (
      <div className="next-page">
        <div className="next-note next-note--error">
          Could not read the master codes.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header">
        <h1>Salto master codes</h1>
        <p className="next-muted">
          One weekly code per SALTO facility, for a user who cannot get in. Rotated every
          Monday; regenerate to bootstrap a facility or to kill a code that has leaked.
        </p>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {(problems.missing > 0 || problems.expired > 0) && (
        <div className="next-note next-note--warn">
          {problems.missing > 0 && (
            <span>
              {problems.missing} {problems.missing === 1 ? 'facility has' : 'facilities have'} no code —
              nobody can be let in there by phone.
            </span>
          )}
          {problems.missing > 0 && problems.expired > 0 && ' '}
          {problems.expired > 0 && (
            <span>
              {problems.expired} {problems.expired === 1 ? 'code has' : 'codes have'} expired and will
              not open a door.
            </span>
          )}
        </div>
      )}

      <section className="next-card">
        <div className="next-card__head">
          <h2>Codes</h2>
          <span className="next-muted">
            {rows == null ? '' : `${visible.length}${visible.length === rows.length ? '' : ` of ${rows.length}`} facilities`}
          </span>
          <span className="next-card__spacer" />
          <button
            type="button"
            className="next-btn next-btn--sm"
            onClick={load}
            disabled={busyAll || !!busyFacilityId}
          >
            Refresh
          </button>
          <button
            type="button"
            className="next-btn next-btn--sm next-btn--danger-solid"
            onClick={regenerateAll}
            disabled={busyAll || !!busyFacilityId || !rows || rows.length === 0}
          >
            {busyAll ? 'Regenerating…' : 'Regenerate all'}
          </button>
        </div>

        {rows == null ? (
          <p className="next-table__note next-muted">Loading…</p>
        ) : (
          <>
            {rows.length > 8 && (
              <div className="next-filters">
                <label className="next-filter">
                  <span className="next-filter__label">Find a facility</span>
                  <input
                    className="next-input"
                    value={filter}
                    placeholder="name, id or code"
                    onChange={event => setFilter(event.target.value)}
                  />
                </label>
              </div>
            )}

            {visible.length === 0 ? (
              <p className="next-table__note next-muted">
                {rows.length === 0 ? 'No SALTO facility has a master code yet.' : 'Nothing matches that.'}
              </p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Facility</th>
                      <th>Master code</th>
                      <th>Valid until</th>
                      <th className="next-table__right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(row => {
                      const end = parseDate(row.endTimeUtc);
                      const expired = !!end && end.getTime() < now;
                      const since = validity(row.endTimeUtc, now);

                      return (
                        <tr key={row.facilityId}>
                          <td data-label="Facility">
                            <span className="next-stack">
                              <span className="next-stack__main">{row.facilityName || '—'}</span>
                              {row.facilityId && (
                                <span className="next-stack__sub">
                                  <Guid value={row.facilityId} to={`/next/facility/${row.facilityId}`} />
                                </span>
                              )}
                            </span>
                          </td>

                          <td data-label="Master code">
                            {row.code ? (
                              <span className="next-code">
                                <span className={`next-code__value${expired ? ' next-code__value--dead' : ''}`}>
                                  {row.code}
                                </span>
                                <button
                                  type="button"
                                  className="next-btn next-btn--quiet next-btn--sm"
                                  onClick={() => copy(row.code, row.facilityId)}
                                >
                                  {copied === row.facilityId ? 'Copied' : 'Copy'}
                                </button>
                              </span>
                            ) : (
                              <span className="next-pill next-pill--bad">none</span>
                            )}
                          </td>

                          <td data-label="Valid until" className="next-nowrap">
                            <span className="next-stack">
                              <span className="next-stack__main">{fmtDate(row.endTimeUtc)}</span>
                              {since && (
                                <span className={`next-stack__sub${expired ? ' next-stack__sub--bad' : ''}`}>
                                  {since.text}
                                </span>
                              )}
                            </span>
                          </td>

                          <td data-label="" className="next-table__right">
                            <button
                              type="button"
                              className="next-btn next-btn--sm"
                              disabled={busyAll || busyFacilityId === row.facilityId}
                              onClick={() => regenerate(row)}
                            >
                              {busyFacilityId === row.facilityId ? '…' : 'Regenerate'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <Modal {...modalProps} />
    </div>
  );
};

MasterCodesInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const MasterCodes = withApi(MasterCodesInternal);

MasterCodes.requiresLogin = true;

export default MasterCodes;
